/**
 * Anti-Fraud Alert System
 * Automatically creates tickets and notifications when fraud is detected
 */

import { supabase } from "../lib/supabase";

/**
 * Creates a fraud alert ticket and staff notification
 * @param {Object} newUser - The new user trying to log in { discordId, name, email, ip }
 * @param {Object[]} fraudMatches - Array of fraud matches from checkFraudMatch
 * @returns {Promise<Object>} - Result of the operation
 */
export const createFraudAlert = async (newUser, fraudMatches) => {
    if (!fraudMatches || fraudMatches.length === 0) {
        return { success: false, error: 'No fraud matches provided' };
    }

    // Build the ticket description with fraud details
    const matchDetails = fraudMatches.map((match, index) => {
        return `
**🚨 Correspondência ${index + 1}:**
- Usuário Banido: ${match.bannedUserName} (ID: ${match.bannedUserId})
- Tipo de Correspondência: ${match.matchReasons.join(', ')}
- Punição Original: ${match.punishment.publicReason || 'Não informado'}
- Aplicada em: ${new Date(match.punishment.appliedAt).toLocaleDateString('pt-BR')}
- ID da Punição: ${match.punishment.id}`;
    }).join('\n');

    const description = `[vermelho:🚨 ALERTA DE FRAUDE - PERFIL VINCULADO]

**Sistema de Anti-Fraude detectou correspondência de dados sensíveis!**

---

[azul:📋 NOVO USUÁRIO TENTANDO ACESSAR]
- **Nome:** ${newUser.name}
- **Discord ID:** ${newUser.discordId}
- **Email:** ${newUser.email || 'Não disponível'}
- **IP:** ${newUser.ip || 'Não disponível'}

---

[laranja:🔗 CORRESPONDÊNCIAS ENCONTRADAS]
${matchDetails}

---

[vermelho:⚠️ AÇÃO RECOMENDADA]
1. Verificar se trata-se do mesmo usuário com conta alternativa
2. Aplicar punição de "Blacklist de Tickets" se confirmado
3. Documentar a investigação no ticket

*Este ticket foi gerado automaticamente pelo Sistema de Anti-Fraude.*`;

    try {
        console.log('[Anti-Fraud] Creating fraud alert ticket...');

        // 1. Create Ticket in Supabase
        const { data: ticket, error: ticketError } = await supabase
            .from('tickets')
            .insert([{
                title: `🚨 ALERTA FRAUDE: Perfil Vinculado Detectado - ${newUser.name}`,
                description: description,
                category: 'triagem',
                priority: 'urgent',
                status: 'aberto',
                type: 'FRAUD_ALERT',
                auxiliary_status: ['triagem'],
                metadata: {
                    fraudType: 'profile_match',
                    newUserId: newUser.discordId,
                    newUserName: newUser.name,
                    matchCount: fraudMatches.length,
                    matchedUsers: fraudMatches.map(m => m.bannedUserId),
                    matchReasons: fraudMatches.flatMap(m => m.matchReasons),
                    detectedAt: new Date().toISOString()
                },
                user_id: newUser.discordId, // Associate with the user being flagged
                user_discord_id: newUser.discordId,
                user_name: newUser.name,
                author_name: 'Sistema Anti-Fraude',
                created_at: new Date().toISOString(),
                last_update: new Date().toISOString()
            }])
            .select()
            .single();

        if (ticketError) throw ticketError;

        console.log('[Anti-Fraud] ✅ Ticket created successfully:', ticket.id);

        // 2. Create Staff Notification in Supabase Announcements
        const notification = {
            title: '🚨 ALERTA: Perfil Vinculado Detectado',
            content: `Novo usuário "${newUser.name}" possui dados correspondentes a ${fraudMatches.length} usuário(s) banido(s).`,
            priority: 'urgent',
            type: 'internal', // Internal so only staff sees it
            visible: true,
            author: 'Sistema Anti-Fraude',
            ticket_id: ticket.id,
            metadata: {
                isFraudAlert: true,
                matchDetails: {
                    newUser: newUser.name,
                    matchedUsers: fraudMatches.map(m => m.bannedUserName),
                    matchReasons: [...new Set(fraudMatches.flatMap(m => m.matchReasons))]
                }
            },
            created_at: new Date().toISOString(),
            read_by: []
        };

        const { error: announcementError } = await supabase
            .from('announcements')
            .insert([notification]);

        if (announcementError) {
            console.error('[Anti-Fraud] Error creating staff notification:', announcementError);
            // Don't fail the whole operation if notification fails
        } else {
            console.log(`[Anti-Fraud] Staff notification created for fraud alert: ${ticket.id}`);
        }

        return { success: true, ticketId: ticket.id };

    } catch (error) {
        console.error('[Anti-Fraud] ❌ Error creating fraud alert:', error);
        return { success: false, error: 'Failed to create fraud alert ticket' };
    }
};

export default { createFraudAlert };
